import {Injectable}               from '@angular/core';
import {AuthService}              from '@src/app/core/services/auth.service';
import {FeatureFlagService}       from '@src/app/core/services/feature-flag.service';
import {Walkthrough, WalkthroughItem} from '@src/app/shared/models/walkthrough.model';
import {ToastrService}            from 'ngx-toastr';
import {IStepOption, TourService} from 'ngx-ui-tour-md-menu';
import {take}                     from 'rxjs/operators';
import {ApiService}               from '../api.service';

@Injectable({
    providedIn: 'root'
})
export class WalkthroughService
{
    public walkthrough: Walkthrough = null;
    private tourFinishedNaturally = false;
    private currentStepPromise: Promise<void> | null = null;

    constructor(
        public tourService: TourService<WalkthroughItem>,
        private featureFlagService: FeatureFlagService,
        private authService: AuthService,
        private apiService: ApiService,
        private toastService: ToastrService
    )
    {
        this.tourService.events$.subscribe(async x => {
            if (x.name === 'stepShow') {
                const stepEvent = x.value as {step?: IStepOption} | undefined;
                if (stepEvent?.step && !stepEvent.step['completed']) {
                    this.currentStepPromise = this.completeStep(stepEvent.step);
                    await this.currentStepPromise;
                }
            }

            if (x.name === 'end') {
                if (this.currentStepPromise) {
                    await this.currentStepPromise;
                }
                if (!this.tourFinishedNaturally) {
                    await this.userDroppedOut(this.walkthrough.id);
                }
            }
        });
    }

    public async start(feature: string, restartTour: boolean): Promise<void>
    {
        this.tourFinishedNaturally = false;
        this.featureFlagService.getFlag('walkthroughs').pipe(take(1)).subscribe({
            next: (flag: boolean) => {
                if (flag) {
                    this.walkthrough = this.getWalkthrough(feature);

                    if (this.walkthrough) {
                        this.setTourSteps(feature, restartTour).then((startWalkthrough: boolean) => {
                            if (startWalkthrough) {
                                try {
                                    // Ensure stale overlays are cleaned up before a fresh start.
                                    this.tourService.end();
                                } catch {
                                    // No active tour to end.
                                }

                                try {
                                    this.tourService.start();
                                } catch (error) {
                                    this.errorMessage();
                                }
                            }
                        }).catch(() => {
                            this.errorMessage();
                        });
                    }
                }
            }
        });
    }

    public async reset(): Promise<void>
    {
        return new Promise((resolve, reject) => {
            this.apiService.get('walkthroughs/reset').subscribe({
                next: (response) => {
                    this.authService.walkthroughs = response.data;
                    resolve();
                },
                error: (error) => {
                    this.errorMessage();
                    reject(error);
                }
            });
        });
    }

    private async setTourSteps(feature: string, restartTour: boolean): Promise<boolean>
    {
        if (this.walkthrough.has_dropped_out || (this.walkthrough.is_completed && !restartTour)) {
            return false;
        }

        if (restartTour) {
            await this.restartTour(feature);
            this.walkthrough = this.getWalkthrough(feature);
        }

        if (this.walkthrough && this.walkthrough.items.length > 0) {
            const steps = this.walkthrough.items.map(item => {
                const extra = item.data ? (typeof item.data === 'string' ? JSON.parse(item.data) : item.data) : {};
                return { ...item, ...extra };
            });

            this.tourService.initialize(steps, {
                enableBackdrop: false,
                closeOnOutsideClick: false,
            });

            return true;
        }

        return false;
    }

    private completeStep(step: IStepOption): Promise<void>
    {
        return new Promise((resolve, reject) => {
            this.apiService.get(`walkthrough-items/${step.stepId}/complete`).subscribe({
                next: (response) => {
                    this.authService.walkthroughs = response.data;
                    this.walkthrough = this.getWalkthrough(this.walkthrough.feature);
                    if (this.walkthrough?.is_completed) {
                        this.tourFinishedNaturally = true;
                    }
                    resolve();
                },
                error: (error) => reject(error)
            });
        });
    }

    private async userDroppedOut(walkthroughId: string): Promise<void>
    {
        return new Promise((resolve, reject) => {
            this.apiService.get(`walkthroughs/${walkthroughId}/drop-out`).subscribe({
                next: (response) => {
                    this.authService.walkthroughs = response.data;
                    resolve();
                },
                error: (error) => {
                    this.errorMessage();
                    reject(error);
                }
            });
        });
    }

    private restartTour(feature: string): Promise<boolean>
    {
        return new Promise((resolve, reject) => {
            this.apiService.get(`walkthroughs/${feature}/reset`).subscribe({
                next: (response) => {
                    this.authService.walkthroughs = response.data;
                    resolve(true);
                },
                error: (error) => {
                    this.errorMessage();
                    reject(error);
                }
            });
        });
    }

    private getWalkthrough(feature: string): Walkthrough | null
    {
        let walkthrough: Walkthrough = this.authService.walkthroughs.find(walkthrough => walkthrough.feature === feature);
        return walkthrough ?? null;
    }

    private errorMessage(): void
    {
        this.toastService.error('An error occurred while starting the walkthrough.');
    }
}
